
import { defineComponent } from "vue";
import type { PropType } from "vue";

// Body scroll lock shared by all open modals (nested/stacked safe).
let lockCount = 0;
let previousOverflow = "";
function lockScroll() {
  if (lockCount++ === 0) {
    previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }
}
function unlockScroll() {
  if (lockCount > 0 && --lockCount === 0) {
    document.body.style.overflow = previousOverflow;
  }
}

/**
 * Shared modal: dark overlay + `modal-content bg-dark text-light` card.
 * Escape and (optionally) backdrop click emit `close`; body scroll is locked while open.
 */
export default defineComponent({
  name: "BaseModal",
  props: {
    show: { type: Boolean, default: true },
    /** When set, renders the standard title bar with a close button. */
    title: { type: String, default: "" },
    size: { type: String as PropType<"sm" | "md" | "lg">, default: "md" },
    closeOnBackdrop: { type: Boolean, default: true },
    /** CEF mode: overlay constrained to the in-game browser container. */
    cef: { type: Boolean, default: false },
    /** Ask for a tall card (min-height 80vh). */
    tall: { type: Boolean, default: false },
    contentClass: { type: String, default: "" },
  },
  emits: ["close"],
  data() {
    return { active: false };
  },
  watch: {
    show: {
      immediate: true,
      handler(visible: boolean) {
        if (visible) this.activate();
        else this.deactivate();
      },
    },
  },
  beforeUnmount() {
    this.deactivate();
  },
  methods: {
    onBackdropClick() {
      if (this.closeOnBackdrop) this.$emit("close");
    },
    onKeydown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      // Let an open sweetalert dialog consume Escape first.
      if (document.querySelector(".swal-overlay--show-modal")) return;
      this.$emit("close");
    },
    activate() {
      if (this.active) return;
      this.active = true;
      lockScroll();
      document.addEventListener("keydown", this.onKeydown);
      this.$nextTick(() => {
        const el = this.$refs.overlayRef as HTMLElement | undefined;
        if (el && !el.contains(document.activeElement)) el.focus();
      });
    },
    deactivate() {
      if (!this.active) return;
      this.active = false;
      unlockScroll();
      document.removeEventListener("keydown", this.onKeydown);
    },
  },
});
